import { capitalize, groupBy } from 'lodash';
import { SemanticEssentialColor } from '@spotify-internal/encore-web';
import { TFunction } from '@spotify-internal/i18n-core';
import { parseURI, URITypeMap } from '@spotify-internal/uri';
import { DeliveryStatus } from '@hooks/types';
import {
  Album,
  AlbumAvailabilityByTerritory,
  Artist,
  ArtistRole,
  AvailabilityStatus,
  MediaType,
} from '@hooks/useAlbumInfo/types';
import { ArtistAlbum } from '@hooks/useArtistAppearsOn/types';
import { SearchType } from '@hooks/useSearchedAlbums/types';
import { TrackMediaType } from '@hooks/useTrackInfo/types';
import { Timestamp } from '../../generated/grpc/google/protobuf';
import { AVAILABLE, UNAVAILABLE, VARIOUS_ARTIST_THRESHOLD } from './constants';

const getSeconds = (ms: number): number => {
  return Math.floor((ms / 1000) % 60);
};

const getMinutes = (ms: number): number => {
  return Math.floor((ms / (1000 * 60)) % 60);
};

export const getHours = (ms: number): number => {
  return Math.floor(ms / (1000 * 60 * 60));
};

export const isEmptyString = (str: string): boolean => {
  return typeof str === 'string' && str.trim() === '';
};

export const msToFormattedDuration = (
  ms: number,
): { hours: number; minutes: number; seconds: number } => {
  const seconds = getSeconds(ms);
  const minutes = getMinutes(ms);
  const hours = getHours(ms);

  return {
    hours,
    minutes,
    seconds,
  };
};

export const msToTimestampFormattedDuration = (duration: number): string => {
  const seconds = getSeconds(duration);
  const minutes = getMinutes(duration);
  const hours = getHours(duration);

  const paddedSeconds = seconds.toString().padStart(2, '0');
  const paddedMinutes = minutes.toString().padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${paddedMinutes}:${paddedSeconds}`;
  }
  return `${minutes}:${paddedSeconds}`;
};

export const formatBytes = (bytes: number, decimals = 1) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export const buildAlbumUriFromId = (id: string): string => {
  if (id.includes('spotify:album:')) {
    return id;
  }

  return `spotify:album:${id}`;
};

export const buildTrackUriFromId = (id: string): string => {
  if (id.includes('spotify:track:')) {
    return id;
  }

  return `spotify:track:${id}`;
};

export const buildArtistUriFromId = (id: string): string => {
  if (id.includes('spotify:artist:')) {
    return id;
  }

  return `spotify:artist:${id}`;
};

export const getUriId = (uri: string): string => {
  const uriParts = uri.split(':');
  const uriId = uriParts[uriParts.length - 1];
  return uriId;
};

export const sortArtistsByRole = (artists: Artist[]) => {
  const artistRolesOrder = [
    ArtistRole.MAIN_ARTIST,
    ArtistRole.FEATURED_ARTIST,
    ArtistRole.COMPOSER,
    ArtistRole.REMIXER,
    ArtistRole.PRODUCER,
    ArtistRole.LYRICIST,
    ArtistRole.ORCHESTRA,
    ArtistRole.ARRANGER,
    ArtistRole.CONDUCTOR,
    ArtistRole.WRITER,
    ArtistRole.AUTHOR,
    ArtistRole.ACTOR,
    ArtistRole.UNKNOWN,
  ];
  return artists.sort((a, b) => {
    return artistRolesOrder.indexOf(a.role) - artistRolesOrder.indexOf(b.role);
  });
};

export const formatCasingOfReleaseType = (type: string) => {
  const typeUpperCase = type.toLocaleUpperCase();
  if (typeUpperCase === 'EP') {
    return typeUpperCase;
  }
  return capitalize(type);
};

export const getMediaType = (
  type: MediaType | TrackMediaType,
  t: TFunction,
) => {
  switch (type) {
    case MediaType.MEDIA_TYPE_AUDIO_ONLY:
    case TrackMediaType.MEDIA_TYPE_AUDIO:
      return t('atlas.media_type.audio', 'Audio', 'Text for Audio media type');
    case MediaType.MEDIA_TYPE_VIDEO_ONLY:
    case TrackMediaType.MEDIA_TYPE_VIDEO:
      return t('atlas.media_type.video', 'Video', 'Text for Video media type');
    default:
      return t('atlas.media_type.audio', 'Audio', 'Text for Audio media type');
  }
};

export const getBase62FromUrlOrUri = (url: string) => {
  const parsedURI = parseURI(url);

  if (!parsedURI) {
    return '';
  }

  return parsedURI.id;
};

export const getUriType = (uri: string) => {
  const parsedURI = parseURI(uri.toLocaleLowerCase());

  if (!parsedURI) {
    return '';
  }

  switch (parsedURI.type) {
    case URITypeMap.ALBUM:
      return SearchType.ALBUM_URI;
    case URITypeMap.ARTIST:
      return SearchType.ARTIST_URI;
    default:
      return '';
  }
};

export const getUrlType = (url: string) => {
  // captures e.g. *://open.spotify.com/album/a234567c91b34567891234
  const captureFullSpotifyUrl =
    '://(play|open).spotify.com/([a-z]+)/([A-Za-z0-9]{22})';
  // captures e.g. *://open.spotify.com/album/
  const capturePartialSpotifyUrl = '://(play|open).spotify.com/([a-z]+)/';
  const fullSpotifyUrlRegex = new RegExp(captureFullSpotifyUrl);
  const partialSpotifyUrlRegex = new RegExp(capturePartialSpotifyUrl);

  if (!fullSpotifyUrlRegex.test(url)) {
    return '';
  }

  // split result e.g. ['https', 'open', 'album', 'a234567c91b34567891234']
  const urlType = url.split(partialSpotifyUrlRegex)[2];

  // We only have support for album/artist URL/URI and track URL types at the moment
  switch (urlType) {
    case 'album':
      return SearchType.ALBUM_URL;
    case 'artist':
      return SearchType.ARTIST_URL;
    case 'track':
      return SearchType.TRACK_URL;
    default:
      return '';
  }
};

export const isSearchTermBulkSearch = (searchTerm: string): Boolean => {
  const searchTermFirstCharacter = searchTerm[0];
  const searchTermLAstCharacter = searchTerm[searchTerm.length - 1];

  if (searchTermFirstCharacter === '"' && searchTermLAstCharacter === '"') {
    return false;
  }

  if (searchTerm.split(',').length > 1) {
    return true;
  }

  return false;
};

export const getSearchType = (searchTerm: string) => {
  const isBulkSearch = isSearchTermBulkSearch(searchTerm);

  if (isBulkSearch) {
    return SearchType.BULK_SEARCH;
  }

  const urlType = getUrlType(searchTerm);

  if (urlType) {
    return urlType;
  }

  const uriType = getUriType(searchTerm);

  if (uriType) {
    return uriType;
  }

  return SearchType.FREE_TEXT;
};

export class MultiRoleArtist {
  uri: string;
  name: string;
  roles: string[];
  isVerified: boolean;

  constructor(uri: string, name: string, roles: string[], isVerified: boolean) {
    this.uri = uri;
    this.name = name;
    this.roles = roles;
    this.isVerified = isVerified;
  }
}

export const convertToTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

export const groupUniqueArtistsByRole = (artists: Artist[]) => {
  const groupedArtists = groupBy(artists, 'uri');

  return Object.values(groupedArtists).map(
    artistWithSameId =>
      new MultiRoleArtist(
        artistWithSameId[0].uri,
        artistWithSameId[0].name,
        artistWithSameId.map(artist => convertToTitleCase(artist.role)),
        artistWithSameId[0].isVerified || false,
      ),
  );
};

export const sortByORDateAsc = (
  a: Album | ArtistAlbum,
  b: Album | ArtistAlbum,
) => {
  const dateA = new Date(a.originalReleaseDate);
  const dateB = new Date(b.originalReleaseDate);
  if (dateA < dateB) {
    return -1;
  }
  if (dateA > dateB) {
    return 1;
  }
  return 0;
};

export const sortByORDateDesc = (
  a: Album | ArtistAlbum,
  b: Album | ArtistAlbum,
) => {
  const dateA = new Date(a.originalReleaseDate);
  const dateB = new Date(b.originalReleaseDate);
  if (dateA > dateB) {
    return -1;
  }
  if (dateA < dateB) {
    return 1;
  }
  return 0;
};

export const copyTableToClipboard = (tableElem: Element) => {
  let range;
  let sel;
  // Check range and selection are supported by the browser
  if (document.createRange() && window.getSelection() && tableElem) {
    range = document.createRange();
    sel = window.getSelection();
    // Unselect any element in the page
    sel?.removeAllRanges();
    try {
      range.selectNodeContents(tableElem);
      sel?.addRange(range);
    } catch (e) {
      range.selectNode(tableElem);
      sel?.addRange(range);
    }
  }

  try {
    const type = 'text/html';
    const html = tableElem?.outerHTML || '';
    const blob = new Blob([html as string], { type });

    // We prefer to use the Clipboard API as it is the successor of document.execCommand():
    // https://developer.mozilla.org/en-US/docs/Web/API/Document/execCommand
    if (navigator.clipboard.write) {
      navigator.clipboard.write([new ClipboardItem({ [type]: blob })]);
    } else {
      document.execCommand('copy');
    }
  } catch (error) {
    // eslint-disable-next-line
    console.log('Error copying table to navigator clipboard: ', error);
  }
};

export const isArtistsReplacedWithVariousArtists = (
  deliveredArtists: Artist[],
  effectiveArtists: Artist[],
) => {
  const mainArtistsOrRemixers = deliveredArtists.filter((artist: Artist) => {
    const trimmedArtistRole = artist.role.toLowerCase().replace(/\s/g, '');
    return (
      trimmedArtistRole === 'mainartist' || trimmedArtistRole === 'remixer'
    );
  });
  const hasVariousArtistsEffectiveData = effectiveArtists.findIndex(
    artist => artist.name.toLowerCase().replace(/\s/g, '') === 'variousartists',
  );

  return (
    mainArtistsOrRemixers.length >= VARIOUS_ARTIST_THRESHOLD &&
    hasVariousArtistsEffectiveData > -1
  );
};

export const getStatusIndicatorColor = (status: String) => {
  let color: SemanticEssentialColor;

  switch (status) {
    case DeliveryStatus.SUCCEEDED:
      color = 'essentialPositive';
      break;
    case DeliveryStatus.PENDING:
      color = 'essentialBase';
      break;
    case DeliveryStatus.FAILED:
      color = 'essentialNegative';
      break;
    default:
      color = 'essentialSubdued';
      break;
  }

  return color;
};

export const formatAvailabilityStatus = (status: string) => {
  let statusText: string = UNAVAILABLE;
  let isFlagged: boolean = false;

  if (status === AvailabilityStatus.DELIVERED) {
    statusText = AVAILABLE;
  } else if (status === AvailabilityStatus.DELIVERED_FLAGGED) {
    statusText = AVAILABLE;
    isFlagged = true;
  } else if (status === AvailabilityStatus.UNAVAILABLE_FLAGGED) {
    isFlagged = true;
  }

  return {
    statusText,
    isFlagged,
  };
};

export const formatTerritories = (
  availability: AlbumAvailabilityByTerritory,
) => {
  return Object.keys(availability)
    .sort((a, b) => a.localeCompare(b))
    .map(territoryCode => {
      return { [territoryCode]: availability[territoryCode] };
    });
};

export const hasUnavailableTerritories = (
  territoriesAvailability: AlbumAvailabilityByTerritory,
) => {
  let unavailableTerritoriesCount = 0;
  const isoCodeAndAvailabilityArray = Object.entries(territoriesAvailability);

  for (const [isoCode, availabilityData] of isoCodeAndAvailabilityArray) {
    if (
      (availabilityData.status === AvailabilityStatus.UNAVAILABLE_FLAGGED ||
        availabilityData.status === AvailabilityStatus.DELIVERED_FLAGGED) &&
      // TEMPORARY - YOUX-372 - Hiding patch alerts when caused by clearance patches during employee testing
      !(availabilityData.patches.length && isoCode === 'US')
    ) {
      unavailableTerritoriesCount++;
    }
  }

  if (unavailableTerritoriesCount > 0) {
    return true;
  }

  return false;
};

export const dateFromProtoTimestamp = (timestamp?: Timestamp | null) => {
  if (!timestamp) {
    return { date: '', time: '' };
  }

  const secs = parseInt(timestamp.seconds as string, 10);
  const dateISOString = new Date(secs * 1000).toISOString();
  const date = dateISOString.substring(0, 10);
  const time = dateISOString.substring(11, 19);

  return { date, time };
};

export const translateRole = (
  type: string,
  untranslatedText: string,
  t: TFunction,
) => {
  switch (type.toUpperCase()) {
    case 'MAIN ARTIST':
      return t(
        'atlas.labels.roles.main_artist',
        'Main artist',
        'A type of artist role',
      );
    case 'FEATURED ARTIST':
      return t(
        'atlas.labels.roles.featured_artist',
        'Featured artist',
        'A type of artist role',
      );
    case 'COMPOSER':
      return t(
        'atlas.labels.roles.composer',
        'Composer',
        'A type of artist role',
      );
    case 'REMIXER':
      return t(
        'atlas.labels.roles.remixer',
        'Remixer',
        'A type of artist role',
      );
    case 'PRODUCER':
      return t(
        'atlas.labels.roles.producer',
        'Producer',
        'A type of artist role',
      );
    case 'LYRICIST':
      return t(
        'atlas.labels.roles.lyricist',
        'Lyricist',
        'A type of artist role',
      );
    case 'ORCHESTRA':
      return t(
        'atlas.labels.roles.orchestra',
        'Orchestra',
        'A type of artist role',
      );
    case 'ARRANGER':
      return t(
        'atlas.labels.roles.arranger',
        'Arranger',
        'A type of artist role',
      );
    case 'CONDUCTOR':
      return t(
        'atlas.labels.roles.conductor',
        'Conductor',
        'A type of artist role',
      );
    case 'WRITER':
      return t('atlas.labels.roles.writer', 'Writer', 'A type of artist role');
    case 'AUTHOR':
      return t('atlas.labels.roles.author', 'Author', 'A type of artist role');
    case 'ACTOR':
      return t('atlas.labels.roles.actor', 'Actor', 'A type of artist role');
    case 'RELEASE':
      return t(
        'atlas.breadcrumbs.release',
        'Release',
        'Text for a breadcrumb link. Refers to "Release" in the noun form',
      );
    case 'TRACK':
      return t(
        'atlas.breadcrumbs.track',
        'Track',
        'Text for a breadcrumb link. Refers to "Track" in the noun form',
      );
    default:
      return untranslatedText;
  }
};

export const translateBlockedReason = (reason: string, t: TFunction) => {
  switch (reason.toUpperCase()) {
    case 'ARTIST WATCHLIST':
      return t(
        'atlas.labels.blocked_reason.artist_watchlist',
        'Artist watchlist',
        'A list of potentially blocked artists whose content requires approval to upload',
      );
    default:
      return reason;
  }
};
